import { useState, useCallback, useEffect } from 'react';

type Theme = 'light' | 'dark';

const STORAGE_KEY = 'sorvaine-theme';

function getCurrentTheme(): Theme {
  return document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
}

export function useTheme() {
  const [theme, setTheme] = useState<Theme>(getCurrentTheme);

  // Re-sync local state if the data-theme attribute changes externally
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const next = getCurrentTheme();
      setTheme((prev) => (prev !== next ? next : prev));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => observer.disconnect();
  }, []);

  const toggleTheme = useCallback(() => {
    const next: Theme = getCurrentTheme() === 'dark' ? 'light' : 'dark';

    // Temporarily disable ALL transitions for a clean instant snap
    const style = document.createElement('style');
    style.id = 'disable-transitions';
    style.textContent = '*, *::before, *::after { transition: none !important; }';
    document.head.appendChild(style);

    // Apply theme
    if (next === 'light') {
      document.documentElement.setAttribute('data-theme', 'light');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    localStorage.setItem(STORAGE_KEY, next);
    setTheme(next);

    // Re-enable transitions after paint completes
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.getElementById('disable-transitions')?.remove();
      });
    });
  }, []);

  return { theme, toggleTheme } as const;
}
